import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * Creates a
 * {@link https://en.wikipedia.org/wiki/Logical_disjunction | logical disjunction}
 *
 * @param expressions List of expressions to disjoin
 * @example
 * {
 *   '{}': {
 *     'isActive': { '@a': 'is_active' },
 *     'isDirect': { '@a': 'is_direct' },
 *     'isActiveOrDirect': { or: [ { '@': 'isActive' }, { '@': 'isDirect' } ] }
 *   }
 * }
 * // ->
 * { isActive: true, isDirect: true, isActiveOrDirect: true },
 * { isActive: true, isDirect: false, isActiveOrDirect: true },
 * { isActive: false, isDirect: true, isActiveOrDirect: true },
 * { isActive: false, isDirect: false, isActiveOrDirect: false },
 * ...
 */
export interface Or<out T = any> {
  /** {@link Or} */
  readonly [Key.Or]: T extends boolean ? readonly Expression<boolean>[] : never
}
export namespace Or {
  export const isOr: TypeChecker<Or> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Or>({
      [Key.Or]: CommonArgs.isListOfExpressions as TypeChecker<Or[Key.Or]>,
    }),
  ).usingAlias('Fn.Or')
  export const assertIsOr: TypeAsserter<Or> = TypeAsserter.Factory.from(isOr)

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.Or,
    readonly fnNode.Node<boolean>[]
  > {
    public readonly type = common.value.types.Intrinsic.Boolean

    constructor(arg: Node<T>['arg']) {
      super(Key.Or, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Or>['expressionsInArgs'] = fn => fn[Key.Or]
  export const HELPER: FnHelper<Or> = {
    createNode: standardNodeCreator.usingExpressionList({
      // Empty disjunctions (or disjunctions with all falses) are false
      emptyValue: false,
      finalizeArgs: (args: fnNode.Node<boolean>[]) => args,
      getArgExpressions: expressionsInArgs,
      // Any truthy values cause the whole disjunction to be true
      handleValue: node => (node.value ? { return: true } : undefined),
      nodeClass: Node,
      setupStash: () => undefined,
    }),
    expressionsInArgs,
    typeChecker: isOr,
  }
}
